import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  Button,
  Chip,
  FONTS,
  Form,
  HStack,
  MobileHeader as GarnishMobileHeader,
  Modal,
  theme,
  useBreakpointHelpers,
  useFluidSize,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type {
  AddressTypeProps,
  DeliveryNoteProps,
  FooterProps,
  FormFieldProps,
  HeaderProps,
} from '../AddressForm.types';
import { DELIVERY_PREFERENCE } from '../constants';
import { useDuplicateCustomerAddressValidation } from '../hooks';
import { TooltipChip } from './AddressForm.components.tooltipChip';

export const DeliveryNotes = (props: DeliveryNoteProps) => {
  const { form, notes, maxLength } = props;

  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useBreakpointHelpers();

  return (
    <View
      style={[
        styles.deliveryNotes,
        currentBreakpoint.isMobile && styles.deliveryNotesMobile,
      ]}
    >
      <View style={styles.header}>
        <BodyText style={styles.text}>
          {t('account.addresses.placeholder.delivery-note-a11y-label')}
        </BodyText>
        <BodyText
          style={[
            notes?.length === maxLength && styles.cautionText,
            styles.text,
            styles.smallText,
          ]}
        >
          {notes?.length}/{maxLength}
        </BodyText>
      </View>

      <Form.TextField
        name="notes"
        control={form.control}
        testID="account.address-form.delivery-notes"
        style={styles.inputField}
        maxLength={maxLength}
        placeholder={t('account.addresses.placeholder.delivery-note')}
        accessibilityLabel={t(
          'account.addresses.placeholder.delivery-note-a11y-label',
        )}
        accessibilityHint={t(
          'account.addresses.placeholder.delivery-note-a11y-label',
        )}
        multiline
        blurOnSubmit
        returnKeyType="done"
      />
    </View>
  );
};

export const DeliveryPreference = ({ form, updateField }: FormFieldProps) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useBreakpointHelpers();
  const fluid = useFluidSize({ min: 375, max: 425 });
  const paddingHorizontal = fluid(12, 16);
  const fluidPadding = useStyle(
    () => ({ paddingHorizontal }),
    [paddingHorizontal],
  );

  return (
    <View
      style={currentBreakpoint.isMobile ? styles.mobileSections : undefined}
    >
      <BodyText style={styles.text}>{t('account.addresses.drop-off')}</BodyText>
      <Chip.Menu
        value={form.deliveryPreference}
        onChange={updateField('deliveryPreference')}
      >
        <Chip
          value={DELIVERY_PREFERENCE.LEAVE_AT_DOOR}
          style={fluidPadding}
          testID="account.address-form.leave-at-door"
          accessibilityHint={t(
            'account.addresses.delivery-preference.leave-at-door',
          )}
          accessibilityLabel={t(
            'account.addresses.delivery-preference.leave-at-door',
          )}
        >
          {t('account.addresses.delivery-preference.leave-at-door')}
        </Chip>
        <Chip
          value={DELIVERY_PREFERENCE.DOOR}
          style={fluidPadding}
          testID="account.address-form.meet-at-door"
          accessibilityHint={t(
            'account.addresses.delivery-preference.meet-at-door',
          )}
          accessibilityLabel={t(
            'account.addresses.delivery-preference.meet-at-door',
          )}
        >
          {t('account.addresses.delivery-preference.meet-at-door')}
        </Chip>
        <Chip
          value={DELIVERY_PREFERENCE.CURBSIDE}
          style={fluidPadding}
          testID="account.address-form.meet-outside"
          accessibilityHint={t(
            'account.addresses.delivery-preference.meet-outside',
          )}
          accessibilityLabel={t(
            'account.addresses.delivery-preference.meet-outside',
          )}
        >
          {t('account.addresses.delivery-preference.meet-outside')}
        </Chip>
      </Chip.Menu>
    </View>
  );
};

export const AddressType = ({
  addressType,
  updateField,
  setAddressType,
}: AddressTypeProps) => {
  const { t } = useLocalizationContext();

  const { checkIfDuplicateAddressByName } =
    useDuplicateCustomerAddressValidation();
  const shouldDisableHomeChip =
    addressType !== 'home' && checkIfDuplicateAddressByName('home');
  const shouldDisableWorkChip =
    addressType !== 'work' && checkIfDuplicateAddressByName('work');

  return (
    <View>
      <Chip.Menu
        value={addressType}
        onChange={(type) => {
          const validType = ['home', 'work'].includes(type) ? type : '';

          updateField('name')(validType);
          setAddressType(type);
        }}
      >
        <TooltipChip
          value="home"
          showTooltip={shouldDisableHomeChip}
          accessibilityLabel={t('account.addresses.address-type.home')}
          accessibilityHint={t('account.addresses.address-type.home')}
          testID="account.address-form.address-type.home"
        >
          <BodyText>{t('account.addresses.address-type.home')}</BodyText>
        </TooltipChip>
        <TooltipChip
          value="work"
          showTooltip={shouldDisableWorkChip}
          accessibilityLabel={t('account.addresses.address-type.work')}
          accessibilityHint={t('account.addresses.address-type.work')}
          testID="account.address-form.address-type.work"
        >
          <BodyText>{t('account.addresses.address-type.work')}</BodyText>
        </TooltipChip>
        <Chip
          value="custom"
          accessibilityLabel={t('account.addresses.address-type.custom')}
          accessibilityHint={t('account.addresses.address-type.custom')}
          testID="account.address-form.address-type.custom"
        >
          {t('account.addresses.address-type.custom')}
        </Chip>
      </Chip.Menu>
    </View>
  );
};

export const Header = (props: HeaderProps) => {
  const { currentBreakpoint } = useBreakpointHelpers();

  const Responsive = currentBreakpoint.isMobile ? MobileHeader : DesktopHeader;

  return <Responsive {...props} />;
};

const MobileHeader = ({ editing }: HeaderProps) => {
  const { t } = useLocalizationContext();
  const title = editing
    ? t('account.addresses.headline.edit')
    : t('account.addresses.headline.add');

  return (
    <View style={styles.mobileHeaderPadding}>
      <GarnishMobileHeader title={title} />
    </View>
  );
};

const DesktopHeader = ({ editing }: HeaderProps) => {
  const { t } = useLocalizationContext();

  return (
    <Modal.Headline style={styles.modalHeadline}>
      {editing
        ? t('account.addresses.headline.edit')
        : t('account.addresses.headline.add')}
    </Modal.Headline>
  );
};

export const Footer = (props: FooterProps) => {
  const { currentBreakpoint } = useBreakpointHelpers();

  const Responsive = currentBreakpoint.isMobile ? MobileFooter : DesktopFooter;

  return <Responsive {...props} />;
};

const DesktopFooter = ({
  submitCta,
  editing,
  deleting,
  loading,
  submitForm,
  canSubmit,
  onDelete,
}: FooterProps) => {
  const { t } = useLocalizationContext();
  const canDelete = Boolean(editing && onDelete);

  return (
    <View style={styles.footer}>
      <HStack itemsPerRow={canDelete ? 2 : 1}>
        {canDelete ? (
          <Button
            size="large"
            palette="caution"
            isLoading={deleting}
            onPress={onDelete}
            testID="account.address-form.delete-button"
            accessibilityLabel={t('account.addresses.remove-address.label')}
            accessibilityHint={t('account.addresses.remove-address.hint')}
            accessibilityRole="button"
          >
            {t('account.addresses.remove-address.label')}
          </Button>
        ) : null}
        <Button
          size="large"
          isLoading={loading}
          onPress={submitForm}
          disabled={!canSubmit}
          testID="account.address-form.save-button"
          accessibilityLabel={t('account.addresses.save.a11y')}
          accessibilityHint={t('account.addresses.save.a11y')}
          accessibilityRole="button"
        >
          {submitCta ?? t('account.addresses.save')}
        </Button>
      </HStack>
    </View>
  );
};

const MobileFooter = ({
  submitCta,
  editing,
  deleting,
  loading,
  submitForm,
  canSubmit,
  onDelete,
}: FooterProps) => {
  const { t } = useLocalizationContext();
  const canDelete = Boolean(editing && onDelete);

  return (
    <Modal.Footer style={[styles.footer, styles.mobileFooter]} withoutTopBorder>
      <HStack itemsPerRow={canDelete ? 2 : 1}>
        {canDelete ? (
          <Button
            size="large"
            palette="caution"
            isLoading={deleting}
            onPress={onDelete}
            testID="account.address-form.delete-button"
            accessibilityLabel={t('account.addresses.remove-address.label')}
            accessibilityHint={t('account.addresses.remove-address.hint')}
            accessibilityRole="button"
          >
            {t('account.addresses.remove-address.label')}
          </Button>
        ) : null}
        <Button
          size="large"
          isLoading={loading}
          onPress={submitForm}
          disabled={!canSubmit}
          testID="account.address-form.save-button"
          accessibilityLabel={t('account.addresses.save.a11y')}
          accessibilityHint={t('account.addresses.save.a11y')}
          accessibilityRole="button"
        >
          {submitCta ?? t('account.addresses.save')}
        </Button>
      </HStack>
    </Modal.Footer>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.xsmall,
  },
  inputField: {
    height: 80,
  },
  text: {
    fontSize: 12,
    lineHeight: theme.spacing.medium,
    paddingBottom: theme.spacing.small,
    fontFamily: FONTS.SWEET_SANS_TEXT,
  },
  mobileHeaderPadding: {
    paddingTop: theme.spacing.medium,
    paddingHorizontal: theme.spacing.medium,
  },
  deliveryNotes: {
    paddingTop: theme.spacing.large,
  },
  deliveryNotesMobile: {
    paddingTop: theme.spacing.medium + theme.spacing.small,
  },
  mobileSections: {
    paddingTop: theme.spacing.medium,
  },
  modalHeadline: {
    paddingTop: theme.spacing.medium,
  },
  smallText: {
    fontSize: 10,
  },
  cautionText: {
    color: theme.colors.CAUTION,
  },
  mobileFooter: {
    flex: 1,
    alignItems: 'flex-end',
    paddingHorizontal: theme.spacing.medium,
  },
  footer: {
    paddingHorizontal: theme.spacing.xlarge,
    paddingVertical: theme.spacing.large,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
